import { Text, useMediaQuery } from '@ltvco/refresh-lib/theme';
import CloseIcon from '@mui/icons-material/Close';
import { useContext, useEffect, useMemo, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import {
  ChatPageContainer,
  OverlayContainer,
  OverlayContent,
  OverlayHeader,
  ReportWrapper,
} from 'components/DeepSearch/components/components';
import { MonitoringColumn } from 'components/DeepSearch/components/MonitoringColumn';
import { ReportColumn } from 'components/DeepSearch/components/ReportColumn';
import {
  CHAT_COLUMN_WIDTH,
  DEEPSEARCH_ROUTES,
  SEARCH_TYPES,
} from 'components/DeepSearch/constants';
import { DeepSearchLayoutContext } from 'components/DeepSearch/context';
import { ChatLayoutContext } from 'components/DeepSearch/context/ChatLayoutContext';
import { mapReportTypeFromRecentReportsToChatType } from 'components/DeepSearch/utils/utils';

import { LayoutColumnStack, MessagesArea } from './DeepSearchLayout.styles';
import { REPORT_NAV_CONFIG, ReportSideNav } from './ReportSideNav';
import { DeepSearchSidebar } from './Sidebar/DeepSearchSidebar';

const REPORT_TITLES: Record<string, string> = {
  [SEARCH_TYPES.PHONE]: 'Phone Report',
  [SEARCH_TYPES.PROPERTY]: 'Property Report',
  [SEARCH_TYPES.VEHICLE]: 'Vehicle Report',
  [SEARCH_TYPES.EMAIL]: 'Email Report',
  [SEARCH_TYPES.PERSON]: 'Person Report',
  [SEARCH_TYPES.OBITUARY]: 'Obituary Report',
  [SEARCH_TYPES.CONTACT]: 'Contact Report',
  [SEARCH_TYPES.ANCESTOR]: 'Ancestor Report',
  [SEARCH_TYPES.USERNAME]: 'Username Report',
};

interface DeepSearchLayoutProps {
  onLogout: () => void;
}

export const DeepSearchLayout = ({ onLogout }: DeepSearchLayoutProps) => {
  const navigate = useNavigate();
  const { reportState, monitoringState } = useContext(ChatLayoutContext);
  const {
    openReport,
    permalink,
    reportData,
    type,
    subtype,
    closeAllPanels,
    handleCloseReport,
    handleRelatedReportClick,
    openReportFromReportLibrary,
  } = reportState;
  const { sidebarCollapsed, setSidebarCollapsed } = useContext(
    DeepSearchLayoutContext
  );

  const { pathname } = useLocation();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const navKey =
    type === SEARCH_TYPES.VEHICLE && subtype === 'ymm' ? 'vehicle_ymm' : type;
  const navSections = navKey ? REPORT_NAV_CONFIG[navKey] : undefined;
  const handleCloseReportRef = useRef(handleCloseReport);
  handleCloseReportRef.current = handleCloseReport;
  const closeAllPanelsRef = useRef(closeAllPanels);
  closeAllPanelsRef.current = closeAllPanels;

  const reportTitle = type ? REPORT_TITLES[type] ?? 'Report' : '';

  const isMobileBreakpoint = useMediaQuery('(max-width: 1024px)');
  const isMonitoringColumnOpen = monitoringState.selectedMonitor !== null;
  const monitoringColumnWidth = CHAT_COLUMN_WIDTH;
  const monitoringViewMappedType = useMemo(() => {
    const monitor = monitoringState.selectedMonitor;
    if (!monitor) {
      return null;
    }
    return mapReportTypeFromRecentReportsToChatType(monitor.report_type);
  }, [monitoringState.selectedMonitor]);

  const sidebarCollapsedRef = useRef(sidebarCollapsed);
  sidebarCollapsedRef.current = sidebarCollapsed;
  const sidebarCollapsedBeforeOverlay = useRef(false);

  useEffect(() => {
    closeAllPanelsRef.current();
  }, [pathname]);

  useEffect(() => {
    if (openReport) {
      sidebarCollapsedBeforeOverlay.current = sidebarCollapsedRef.current;
      setSidebarCollapsed(true);
    } else {
      setSidebarCollapsed(sidebarCollapsedBeforeOverlay.current);
    }
  }, [openReport, setSidebarCollapsed]);

  const handleRecentReportsClick = () => {
    navigate(DEEPSEARCH_ROUTES.library);
  };

  const handleAccountClick = () => {
    navigate(DEEPSEARCH_ROUTES.account);
  };

  const handleUpgradePlanClick = () => {
    navigate(DEEPSEARCH_ROUTES.upgradePlan);
  };

  const handleMonitoringViewReport = () => {
    const monitor = monitoringState.selectedMonitor;
    if (!monitor || !monitoringViewMappedType) {
      return;
    }
    monitoringState.closeMonitoring();
    openReportFromReportLibrary(monitor.permalink, monitoringViewMappedType);
  };

  const handleMonitoringNewChat = () => {
    monitoringState.closeMonitoring();
    navigate(DEEPSEARCH_ROUTES.chat);
  };

  return (
    <ChatPageContainer>
      <DeepSearchSidebar
        onRecentReportsClick={handleRecentReportsClick}
        onAccountClick={handleAccountClick}
        onUpgradePlanClick={handleUpgradePlanClick}
        onLogout={onLogout}
      />
      <LayoutColumnStack
        isColumnOpen={isMonitoringColumnOpen}
        isMobile={isMobileBreakpoint}
      >
        <MessagesArea
          isColumnOpen={isMonitoringColumnOpen}
          isMobile={isMobileBreakpoint}
        >
          <Outlet />
        </MessagesArea>
        {openReport && (
          <OverlayContainer ref={scrollContainerRef}>
            <OverlayHeader>
              <Text variant="body1" fontWeight="bold">
                {reportTitle}
              </Text>
              <CloseIcon
                sx={{
                  color: 'text.secondary',
                  cursor: 'pointer',
                  fontSize: 20,
                }}
                onClick={handleCloseReport}
              />
            </OverlayHeader>
            <OverlayContent>
              {navSections && (
                <ReportSideNav
                  sections={navSections}
                  scrollContainerRef={scrollContainerRef}
                />
              )}
              <ReportWrapper>
                <ReportColumn
                  reportData={reportData}
                  onClose={handleCloseReport}
                  permalink={permalink}
                  currentWidth="100%"
                  handleRelatedReportClick={handleRelatedReportClick}
                  type={type}
                />
              </ReportWrapper>
            </OverlayContent>
          </OverlayContainer>
        )}
        {monitoringState.selectedMonitor && (
          <MonitoringColumn
            monitor={monitoringState.selectedMonitor}
            currentWidth={isMobileBreakpoint ? '100%' : monitoringColumnWidth}
            onClose={monitoringState.closeMonitoring}
            onViewReport={handleMonitoringViewReport}
            onNewChat={handleMonitoringNewChat}
            isViewReportSupported={Boolean(monitoringViewMappedType)}
            viewReportDisabledTooltip={
              !monitoringViewMappedType
                ? `Unsupported monitor type: ${monitoringState.selectedMonitor.report_type}`
                : undefined
            }
          />
        )}
      </LayoutColumnStack>
    </ChatPageContainer>
  );
};
